import { useState, useEffect } from 'react'
import { Search, Plus } from 'lucide-react'
import ProjectCard from '../components/ProjectCard'
import NewProjectModal from '../components/NewProjectModal'
import { fetchProjects, createProject, deleteProject } from '../api/projectsApi'
import { useWorkspace } from '../../context/WorkspaceContext'
import { SkeletonCardGrid } from '../../shared/ui'

export default function RequirementStudio() {
  const [projects, setProjects] = useState([])
  const [filtered, setFiltered] = useState([])
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [creating, setCreating] = useState(false)
  // The engagement selected in the topbar. Every project belongs to one,
  // so the list follows it and a new project is created against it.
  const { currentEngagementId, engagements } = useWorkspace()
  // The engagement the project will be created under. Its client, contract
  // type and platform are shown read-only in the create form rather than
  // re-typed, and the backend takes them from the engagement regardless.
  const currentEngagement = engagements?.find(
    (e) => e.engagement_id === currentEngagementId
  )

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEngagementId])

  useEffect(() => {
    const q = search.toLowerCase()
    setFiltered(
      q
        ? projects.filter(
            (p) =>
              p.name.toLowerCase().includes(q) ||
              p.client_name?.toLowerCase().includes(q) ||
              p.description?.toLowerCase().includes(q)
          )
        : projects
    )
  }, [search, projects])

  async function load() {
    setLoading(true)
    setError(null)
    try {
      const data = await fetchProjects(currentEngagementId)
      setProjects(data)
    } catch {
      setError('Failed to load projects. Make sure the backend is running.')
    } finally {
      setLoading(false)
    }
  }

  async function handleCreate(formData) {
    setCreating(true)
    try {
      const project = await createProject({
        ...formData,
        engagement_id: currentEngagementId,
      })
      setProjects((prev) => [project, ...prev])
      setShowModal(false)
    } catch {
      alert(
        currentEngagementId
          ? 'Failed to create project.'
          : 'Select a project (engagement) in the top bar first — every RDS project belongs to one.'
      )
    } finally {
      setCreating(false)
    }
  }

  async function handleDelete(projectId) {
    if (!confirm('Delete this project?')) return
    try {
      await deleteProject(projectId)
      setProjects((prev) => prev.filter((p) => p.id !== projectId))
    } catch {
      alert('Failed to delete project.')
    }
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-900">Requirements & Design Studio</h1>
        <p className="text-sm text-gray-500 mt-1">
          End-to-end AI-powered requirements engineering &amp; design across 16 modules and 16 service lines
        </p>
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-3 mb-6">
        <div className="relative flex-1 max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search projects..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-1.5 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus size={15} />
          New Draft
        </button>
      </div>

      {/* States */}
      {loading && <SkeletonCardGrid cards={3} />}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-600">
          {error}
        </div>
      )}

      {!loading && !error && filtered.length === 0 && (
        <div className="text-center py-20 text-gray-400">
          <p className="text-sm">
            {search
            ? 'No projects match your search.'
            : currentEngagementId
              ? 'No projects yet for this engagement. Create your first one!'
              : 'Select a project in the top bar to see its RDS projects.'}
          </p>
        </div>
      )}

      {/* Grid */}
      {!loading && !error && filtered.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((project) => (
            <ProjectCard key={project.id} project={project} onDelete={handleDelete} />
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <NewProjectModal
          onClose={() => setShowModal(false)}
          onSubmit={handleCreate}
          loading={creating}
          engagement={currentEngagement}
        />
      )}
    </div>
  )
}